/* eslint-disable max-len */
import {
    useCallback, useMemo, useRef, useState,
} from 'react';
import { Field, FormSpy } from 'react-final-form';
import { useParams } from 'react-router-dom';
import { RenderIf, Tabset } from 'react-rainbow-components';
import { showAppNotification } from '@rainbow-modules/app';
import { isEmpty } from '@rainbow-modules/validation';
import Button from 'components/Button';
import Footer from 'components/Footer';
import InfoIcon from 'components/icons/info';
import Note from 'components/icons/document';
import LocationIcon from 'components/icons/location';
import { Agent } from 'data/firestore/agent/types';
import useUpdateAgentMutation from 'data/hooks/useUpdateAgentMutation';
import getLocation from 'helpers/getLocation';
import resolveTimezone from 'helpers/resolveTimezone';
import useAgentData from 'hooks/useAgentData';
import useUserRoles from 'hooks/useUserRoles';
import { Geometry } from 'types';
import Locations from './Locations';
import AdditionalInfo from './AditionalInfo';
import {
    Container,
    StyledCard,
    PageContainer,
    Header,
    Title,
    StyledForm,
    Row,
    Description,
    FormFooter,
    StyledInput,
    StyledHalfInput,
    StyledGoogleAddressLookup,
    StyledLanguagePicker,
    StyledLoadingShape,
    InputLabel,
    TooltipButton,
    TooltipContainer,
    StyledTab,
    TabLabel,
} from './styled';
import { FormState } from './types';

interface FormValues extends Record<string, unknown> {
    companyName?: string;
    organizationName?: string;
    companyAddress?: Record<string, unknown>;
}

const validateNotEmpty = (value: unknown) => {
    if (isEmpty(value)) return 'This field is required';
    return undefined;
};

const checkFormModified = (formState: FormState<FormValues>): boolean => {
    const { pristine, valid } = formState;
    return pristine || !valid;
};

const mapFormValues = async (currentValues: FormValues) => {
    const {
        companyAddress,
        companyName,
        organizationName,
        aiOutputLanguage,
        customersPreferredLanguage,
    } = currentValues;

    let newCompanyAddress;
    if (companyAddress) {
        const { lat, lng } = getLocation(companyAddress?.geometry as Geometry);
        newCompanyAddress = {
            addressInfo: companyAddress,
            formattedAddress: companyAddress.formatted_address,
            timezone: await resolveTimezone(lat, lng),
        };
    }

    return {
        companyName,
        organizationName,
        aiOutputLanguage,
        customersPreferredLanguage,
        companyAddress: newCompanyAddress,
    };
};

const CompanyInfo = () => {
    const { agentId = '' } = useParams();
    const [disableSubmit, setDisableSubmit] = useState(true);
    const [activeTab, setActiveTab] = useState('additional-info');
    const formRef = useRef();
    const { agent, isLoading: isLoadingAgent } = useAgentData();
    const { role, isAdmin } = useUserRoles();

    const userCanWrite = useMemo(() => isAdmin || ['owner', 'editor'].includes(role as string), [role, isAdmin]);

    const {
        mutateAsync: updateAgent,
        isLoading: isUpdatingAgent,
    } = useUpdateAgentMutation(agentId as string);

    const handleSubmit = useCallback(
        async (values: FormValues) => {
            try {
                const newValues = await mapFormValues(values) as Partial<Pick<Agent, 'companyName' | 'organizationName' | 'companyAddress'>>;
                await updateAgent({
                    body: newValues,
                });
                showAppNotification({
                    title: 'Success',
                    description: 'Your changes have been saved.',
                    icon: 'success',
                    timeout: 5000,
                });
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                formRef.current?.reset();
            } catch (error: any) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
        },
        [updateAgent],
    );

    return (
        <PageContainer>
            <Container>
                <StyledCard>
                    <Header>
                        <Title>Company Info</Title>
                        <Description>
                            A concise overview of the company&apos;s fundamental details, such
                            as its name, address, and preferred language.
                        </Description>
                    </Header>
                    <RenderIf isTrue={isLoadingAgent}>
                        <StyledLoadingShape />
                        <StyledLoadingShape />
                        <StyledLoadingShape />
                        <StyledLoadingShape />
                    </RenderIf>
                    <RenderIf isTrue={!isLoadingAgent}>
                        <StyledForm
                            ref={formRef}
                            id="company-info-form"
                            initialValues={{
                                companyName: agent?.companyName,
                                organizationName: agent?.organizationName,
                                companyAddress: agent?.companyAddress?.addressInfo,
                                aiOutputLanguage: agent?.aiOutputLanguage || 'en',
                                customersPreferredLanguage: agent?.customersPreferredLanguage || 'en',
                            }}
                            onSubmit={handleSubmit}
                        >
                            <Row>
                                <Field
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    component={StyledInput}
                                    name="companyName"
                                    label="Company Name"
                                    labelAlignment="left"
                                    placeholder="My Company"
                                    borderRadius="semi-rounded"
                                    autoComplete="off"
                                    required
                                    readOnly={!userCanWrite}
                                    disabled={isUpdatingAgent}
                                    validate={validateNotEmpty}
                                />
                                <Field
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    component={StyledGoogleAddressLookup}
                                    name="companyAddress"
                                    label="Company Address"
                                    labelAlignment="left"
                                    placeholder="45 Roker Terrace"
                                    borderRadius="semi-rounded"
                                    autoComplete="off"
                                    required
                                    readOnly={!userCanWrite}
                                    disabled={isUpdatingAgent}
                                    validate={validateNotEmpty}
                                />
                            </Row>
                            <Row>
                                <Field
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    component={StyledLanguagePicker}
                                    name="aiOutputLanguage"
                                    label={(
                                        <InputLabel>
                                            AI Output Language
                                            <TooltipButton
                                                icon={<InfoIcon />}
                                                tooltip={(
                                                    <TooltipContainer>
                                                        <p>Sets the language for displaying conversation summaries and inbox titles on the website. Also determines the language used in Email and SMS notifications.</p>
                                                    </TooltipContainer>
                                                )}
                                            />
                                        </InputLabel>
                                    )}
                                    labelAlignment="left"
                                    borderRadius="semi-rounded"
                                    required
                                    readOnly={!userCanWrite}
                                    disabled={isUpdatingAgent}
                                />
                                <Field
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    component={StyledLanguagePicker}
                                    name="customersPreferredLanguage"
                                    label={(
                                        <InputLabel>
                                            Customers Preferred Language
                                            <TooltipButton
                                                icon={<InfoIcon />}
                                                tooltip={(
                                                    <TooltipContainer>
                                                        <p>Sets the language for notifications sent to customers when an relational event is created/updated in an specific calendar.</p>
                                                    </TooltipContainer>
                                                )}
                                            />
                                        </InputLabel>
                                    )}
                                    labelAlignment="left"
                                    borderRadius="semi-rounded"
                                    required
                                    readOnly={!userCanWrite}
                                    disabled={isUpdatingAgent}
                                />
                            </Row>
                            <Row>
                                <Field
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    component={StyledHalfInput}
                                    name="organizationName"
                                    label="Organization Name"
                                    labelAlignment="left"
                                    placeholder="My Organization"
                                    borderRadius="semi-rounded"
                                    autoComplete="off"
                                    readOnly={!userCanWrite}
                                    disabled={isUpdatingAgent}
                                />
                            </Row>
                            <FormSpy
                                onChange={
                                    (props) => setTimeout(
                                        () => setDisableSubmit(checkFormModified(props)),
                                        0,
                                    )
                                }
                            />
                        </StyledForm>
                        <RenderIf isTrue={userCanWrite}>
                            <FormFooter>
                                <Button
                                    label="Save Change"
                                    variant="brand"
                                    form="company-info-form"
                                    type="submit"
                                    borderRadius="semi-rounded"
                                    disabled={disableSubmit}
                                    isLoading={isUpdatingAgent}
                                />
                                <Button
                                    label="Cancel"
                                    variant="outline-brand"
                                    form="company-info-form"
                                    borderRadius="semi-rounded"
                                    disabled={disableSubmit}
                                    isLoading={isUpdatingAgent}
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    onClick={() => formRef.current?.reset()}
                                />
                            </FormFooter>
                        </RenderIf>
                    </RenderIf>
                </StyledCard>
                <StyledCard>
                    <Tabset
                        variant="line"
                        activeTabName={activeTab}
                        onSelect={(e, selectedTab) => setActiveTab(selectedTab)}
                    >
                        <StyledTab
                            name="additional-info"
                            label={(
                                <TabLabel>
                                    <Note />
                                    Additional Info
                                </TabLabel>
                            )}
                        />
                        <StyledTab
                            name="locations"
                            label={(
                                <TabLabel>
                                    <LocationIcon />
                                    Locations
                                </TabLabel>
                            )}
                        />
                    </Tabset>
                    {activeTab === 'additional-info' && (
                        <AdditionalInfo canEditAgent={userCanWrite} />
                    )}
                    {activeTab === 'locations' && (
                        <Locations />
                    )}
                </StyledCard>
            </Container>
            <Footer />
        </PageContainer>
    );
};

export default CompanyInfo;
